import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  settingsService,
  WhatsAppConfigData,
  EmbeddedSignupCallbackData,
  ShopifyConnectData,
  StoreUpdateData,
  CsatSettingsUpdate,
  AiResponsibilityCreate,
  AiResponsibilityUpdate,
} from '../services/settingsService';

export function useWhatsAppStatus() {
  return useQuery({
    queryKey: ['settings', 'whatsapp', 'status'],
    queryFn: () => settingsService.getWhatsAppStatus(),
  });
}

export function useConfigureWhatsApp() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: WhatsAppConfigData) =>
      settingsService.configureWhatsApp(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['settings', 'whatsapp'],
      });
      toast.success('WhatsApp configured successfully');
    },
    onError: (error: any) => {
      const message =
        error.response?.data?.detail || 'Failed to configure WhatsApp';
      toast.error(message);
    },
  });
}

export function useEmbeddedSignupCallback() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: EmbeddedSignupCallbackData) =>
      settingsService.embeddedSignupCallback(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['settings', 'whatsapp'],
      });
      toast.success('WhatsApp connected via Meta Business');
    },
    onError: (error: any) => {
      const message =
        error.response?.data?.detail || 'Failed to complete WhatsApp setup';
      toast.error(message);
    },
  });
}

/** The store's WhatsApp numbers. Any staff member may read them (the inbox
 *  labels conversations by number); only admins may change them. */
export function useWhatsAppNumbers() {
  return useQuery({
    queryKey: ['settings', 'whatsapp', 'numbers'],
    queryFn: () => settingsService.getWhatsAppNumbers(),
    staleTime: 5 * 60 * 1000,
  });
}

function useWhatsAppNumberMutation<TArgs>(
  fn: (args: TArgs) => Promise<unknown>,
  success: string,
  failure: string
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'whatsapp'] });
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      toast.success(success);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || failure);
    },
  });
}

export function useRenameWhatsAppNumber() {
  return useWhatsAppNumberMutation(
    ({ id, label }: { id: string; label: string }) =>
      settingsService.renameWhatsAppNumber(id, label),
    'Number renamed',
    'Failed to rename the number'
  );
}

export function useSetDefaultWhatsAppNumber() {
  return useWhatsAppNumberMutation(
    (id: string) => settingsService.setDefaultWhatsAppNumber(id),
    'Default number changed',
    'Failed to change the default number'
  );
}

export function useDisconnectWhatsAppNumber() {
  return useWhatsAppNumberMutation(
    ({ id, newDefaultId }: { id: string; newDefaultId?: string }) =>
      settingsService.disconnectWhatsAppNumber(id, newDefaultId),
    'Number disconnected',
    'Failed to disconnect the number'
  );
}

export function useConnectBaileys() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => settingsService.connectBaileys(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['settings', 'whatsapp'],
      });
    },
    onError: (error: any) => {
      const message =
        error.response?.data?.detail || 'Failed to start WhatsApp connection';
      toast.error(message);
    },
  });
}

export function useBaileysStatus(enabled: boolean = false) {
  return useQuery({
    queryKey: ['settings', 'baileys', 'status'],
    queryFn: () => settingsService.getBaileysStatus(),
    enabled,
    refetchInterval: enabled ? 3000 : false,
  });
}

export function useDisconnectBaileys() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => settingsService.disconnectBaileys(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['settings', 'whatsapp'],
      });
      queryClient.invalidateQueries({
        queryKey: ['settings', 'baileys', 'status'],
      });
      toast.success('WhatsApp disconnected');
    },
    onError: (error: any) => {
      const message =
        error.response?.data?.detail || 'Failed to disconnect WhatsApp';
      toast.error(message);
    },
  });
}

export function useShopifyStatus() {
  return useQuery({
    queryKey: ['settings', 'shopify', 'status'],
    queryFn: () => settingsService.getShopifyStatus(),
  });
}

export function useConnectShopify() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ShopifyConnectData) =>
      settingsService.connectShopify(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['settings', 'shopify'],
      });
      toast.success('Shopify connected successfully');
    },
    onError: (error: any) => {
      const message =
        error.response?.data?.detail || 'Failed to connect Shopify';
      toast.error(message);
    },
  });
}

export function useSyncShopifyContent() {
  return useMutation({
    mutationFn: () => settingsService.syncShopifyContent(),
    onSuccess: (data) => {
      const parts = [];
      if (data.policies_synced) parts.push(`${data.policies_synced} policies`);
      if (data.pages_synced) parts.push(`${data.pages_synced} pages`);
      if (data.shipping_zones_synced) parts.push(`${data.shipping_zones_synced} shipping zones`);
      const summary = parts.length ? parts.join(', ') : 'No content found';
      toast.success(`Shopify content synced: ${summary}`);
      if (data.warnings?.length) {
        toast.error(`Some content could not be synced. You may need to update your Shopify app scopes and reconnect.`, { duration: 8000 });
      }
    },
    onError: (error: any) => {
      const message =
        error.response?.data?.detail || 'Failed to sync Shopify content';
      toast.error(message);
    },
  });
}

export function useDisconnectShopify() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => settingsService.disconnectShopify(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['settings', 'shopify'],
      });
      toast.success('Shopify disconnected');
    },
    onError: (error: any) => {
      const message =
        error.response?.data?.detail || 'Failed to disconnect Shopify';
      toast.error(message);
    },
  });
}

export function useStoreInfo() {
  return useQuery({
    queryKey: ['settings', 'store'],
    queryFn: () => settingsService.getStore(),
  });
}

export function useUpdateStore() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (update: StoreUpdateData) => settingsService.updateStore(update),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'store'] });
    },
    onError: (error: any) => {
      const message = error.response?.data?.detail || 'Failed to update store';
      toast.error(message);
    },
  });
}

// ── AI Responsibilities ──────────────────────────────────────────────────────
export function useResponsibilities() {
  return useQuery({
    queryKey: ['settings', 'ai-responsibilities'],
    queryFn: () => settingsService.getResponsibilities(),
  });
}

export function useCreateResponsibility() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: AiResponsibilityCreate) => settingsService.createResponsibility(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'ai-responsibilities'] });
      toast.success('Responsibility added');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to add responsibility');
    },
  });
}

export function useUpdateResponsibility() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, update }: { id: string; update: AiResponsibilityUpdate }) =>
      settingsService.updateResponsibility(id, update),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'ai-responsibilities'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to update responsibility');
    },
  });
}

export function useDeleteResponsibility() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => settingsService.deleteResponsibility(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'ai-responsibilities'] });
      toast.success('Responsibility removed');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to remove responsibility');
    },
  });
}

export function useRunResponsibilityNow() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => settingsService.runResponsibilityNow(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'ai-responsibilities'] });
      toast.success('Ran now — check Team Chat for the result');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to run responsibility');
    },
  });
}

export function useClerkFactsSettings() {
  return useQuery({
    queryKey: ['settings', 'clerk-facts'],
    queryFn: () => settingsService.getClerkFactsSettings(),
  });
}

export function useUpdateClerkFactsSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (enabled: boolean) => settingsService.updateClerkFactsSettings(enabled),
    onSuccess: (data) => {
      queryClient.setQueryData(['settings', 'clerk-facts'], data);
    },
    onError: (error: any) => {
      const message = error.response?.data?.detail || 'Failed to update Clerk sharing';
      toast.error(message);
    },
  });
}

export function useCsatSettings() {
  return useQuery({
    queryKey: ['settings', 'csat'],
    queryFn: () => settingsService.getCsatSettings(),
  });
}

export function useUpdateCsatSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (update: CsatSettingsUpdate) => settingsService.updateCsatSettings(update),
    onSuccess: (data) => {
      queryClient.setQueryData(['settings', 'csat'], data);
    },
    onError: (error: any) => {
      const message = error.response?.data?.detail || 'Failed to update feedback settings';
      toast.error(message);
    },
  });
}
